/**
 * Event Types API
 *
 * Functions for interacting with the event types endpoints.
 * Event types are organization-scoped templates used to pre-populate event roles.
 */

import { api, getCurrentOrgSlug } from './client';

// =============================================================================
// Types
// =============================================================================

export interface EventTypeRole {
  id: string;
  eventTypeId: string;
  roleName: string;
  requiredCount: number;
  description?: string | null;
  sortOrder: number;
}

export interface EventType {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  templateRoles: EventTypeRole[];
}

// =============================================================================
// Request/Response Types
// =============================================================================

export interface CreateEventTypeRoleInput {
  roleName: string;
  requiredCount?: number;
  description?: string | null;
  sortOrder?: number;
}

export interface CreateEventTypeInput {
  name: string;
  description?: string | null;
  roles?: CreateEventTypeRoleInput[];
}

export interface UpdateEventTypeInput {
  name?: string;
  description?: string | null;
  isActive?: boolean;
}

export interface UpdateEventTypeRoleInput {
  roleName?: string;
  requiredCount?: number;
  description?: string | null;
  sortOrder?: number;
}

export interface ListEventTypesParams {
  includeInactive?: boolean;
}

// =============================================================================
// Helper
// =============================================================================

/**
 * Get the base URL for event types endpoints
 */
function getEventTypesBaseUrl(): string {
  const orgSlug = getCurrentOrgSlug();
  if (!orgSlug) {
    throw new Error('No organization selected');
  }
  return `/organizations/${orgSlug}/event-types`;
}

// =============================================================================
// Event Types CRUD
// =============================================================================

/**
 * List all event types for the current organization
 */
export async function getEventTypes(
  params?: ListEventTypesParams
): Promise<{ eventTypes: EventType[] }> {
  const response = await api.get<{ eventTypes: EventType[] }>(
    getEventTypesBaseUrl(),
    { params }
  );
  return response.data;
}

/**
 * Get a single event type by ID
 */
export async function getEventType(id: string): Promise<{ eventType: EventType }> {
  const response = await api.get<{ eventType: EventType }>(
    `${getEventTypesBaseUrl()}/${id}`
  );
  return response.data;
}

/**
 * Create a new event type with optional template roles
 */
export async function createEventType(
  data: CreateEventTypeInput
): Promise<{ message: string; eventType: EventType }> {
  const response = await api.post<{ message: string; eventType: EventType }>(
    getEventTypesBaseUrl(),
    data
  );
  return response.data;
}

/**
 * Update an event type
 */
export async function updateEventType(
  id: string,
  data: UpdateEventTypeInput
): Promise<{ message: string; eventType: EventType }> {
  const response = await api.put<{ message: string; eventType: EventType }>(
    `${getEventTypesBaseUrl()}/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete (soft delete) an event type
 */
export async function deleteEventType(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(
    `${getEventTypesBaseUrl()}/${id}`
  );
  return response.data;
}

// =============================================================================
// Template Roles
// =============================================================================

/**
 * Add a template role to an event type
 */
export async function createEventTypeRole(
  eventTypeId: string,
  data: CreateEventTypeRoleInput
): Promise<{ message: string; role: EventTypeRole }> {
  const response = await api.post<{ message: string; role: EventTypeRole }>(
    `${getEventTypesBaseUrl()}/${eventTypeId}/roles`,
    data
  );
  return response.data;
}

/**
 * Update a template role
 */
export async function updateEventTypeRole(
  eventTypeId: string,
  roleId: string,
  data: UpdateEventTypeRoleInput
): Promise<{ message: string; role: EventTypeRole }> {
  const response = await api.put<{ message: string; role: EventTypeRole }>(
    `${getEventTypesBaseUrl()}/${eventTypeId}/roles/${roleId}`,
    data
  );
  return response.data;
}

/**
 * Delete a template role
 */
export async function deleteEventTypeRole(
  eventTypeId: string,
  roleId: string
): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(
    `${getEventTypesBaseUrl()}/${eventTypeId}/roles/${roleId}`
  );
  return response.data;
}
